import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import { Spinner } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Star, Zap, Activity, ClipboardList, Sparkles, Newspaper, GitBranch, Users, ShieldCheck } from "lucide-react";

const FIELDS = [
  { key: "share_events", labelKey: "collab_share_events", icon: Zap },
  { key: "share_hrv", labelKey: "collab_share_hrv", icon: Activity },
  { key: "share_plans", labelKey: "collab_share_plans", icon: ClipboardList },
  { key: "share_ai_reports", labelKey: "collab_share_ai_reports", icon: Sparkles },
  { key: "share_feed_activity", labelKey: "collab_share_feed", icon: Newspaper },
];

export default function Collaboration() {
  const t = useT();
  usePageMeta(t("collaboration_2"));
  const navigate = useNavigate();
  const [coaches, setCoaches] = useState(null);
  const [perms, setPerms] = useState({});
  const [innerCircleIds, setInnerCircleIds] = useState([]);

  const load = useCallback(async () => {
    const [mine, dp] = await Promise.all([
      api.get("/auth/my-coaches").then((r) => r.data).catch(() => []),
      api.get("/collab/data-permissions").then((r) => r.data).catch(() => ({ permissions: [], inner_circle_coach_ids: [] })),
    ]);
    const map = {};
    for (const p of dp.permissions || []) map[p.coach_id] = p;
    setPerms(map);
    setInnerCircleIds(dp.inner_circle_coach_ids || []);
    setCoaches(Array.isArray(mine) ? mine : []);
  }, []);
  useEffect(() => { load(); }, [load]);

  const inCircle = (id) => innerCircleIds.includes(id);

  const toggle = async (coachId, key, value) => {
    if (!inCircle(coachId)) { toast.error(t("collab_join_first")); return; }
    setPerms((p) => ({ ...p, [coachId]: { ...(p[coachId] || {}), [key]: value } }));
    try { await api.patch("/collab/data-permissions", { coach_id: coachId, [key]: value }); toast.success(t("collab_saved")); }
    catch (e) { toast.error(apiError(e)); setPerms((p) => ({ ...p, [coachId]: { ...(p[coachId] || {}), [key]: !value } })); }
  };

  const toggleCollab = async (coachId, otherCoachId, on) => {
    const cur = perms[coachId]?.collab_with_coach_ids || [];
    const next = on ? Array.from(new Set([...cur, otherCoachId])) : cur.filter((x) => x !== otherCoachId);
    setPerms((p) => ({ ...p, [coachId]: { ...(p[coachId] || {}), collab_with_coach_ids: next } }));
    try { await api.patch("/collab/data-permissions", { coach_id: coachId, collab_with_coach_ids: next }); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="collaboration-screen">
      <PageHeader eyebrow={t("nav.group.book")} title={t("collaboration_2")} subtitle={t("decide_who_sees_what_only_coaches_whose")} />

      {coaches === null ? (
        <Spinner label={t("common.loading")} />
      ) : coaches.length === 0 ? (
        <EmptyState icon={Users} title={t("you_re_not_connected_to_any_coach_yet")} />
      ) : (
        <div className="space-y-4">
          {coaches.map((c) => {
            const p = perms[c.id] || {};
            const gated = !inCircle(c.id);
            const others = coaches.filter((x) => x.id !== c.id && inCircle(x.id));
            return (
              <div key={c.id} data-testid={`collab-coach-${c.id}`} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-base text-foreground">{c.name}</span>
                      {!gated && (
                        <span data-testid={`inner-circle-badge-${c.id}`} className="inline-flex items-center gap-1 rounded-md bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                          <Star className="h-2.5 w-2.5 fill-current" /> {t("inner_circle_2")}
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs font-medium text-primary">{gated ? t("collab_not_in_circle") : t("collab_active")}</p>
                  </div>
                  {gated && (
                    <button onClick={() => navigate(`/coaches/${c.id}`)} data-testid={`join-circle-${c.id}`}
                      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90">
                      <Star className="h-3.5 w-3.5" /> {t("collab_join")}
                    </button>
                  )}
                </div>

                <div className="mt-2 divide-y divide-border">
                  {FIELDS.map((f) => (
                    <div key={f.key} className="flex items-center gap-3 py-2.5">
                      <f.icon className="h-4 w-4 shrink-0 text-primary" />
                      <span className={`flex-1 text-sm ${gated ? "text-muted-foreground/60" : "text-foreground"}`}>{t(f.labelKey)}</span>
                      <Switch data-testid={`toggle-${f.key}-${c.id}`} checked={!gated && !!p[f.key]} disabled={gated} onCheckedChange={(v) => toggle(c.id, f.key, v)} />
                    </div>
                  ))}
                </div>

                {!gated && others.length > 0 && (
                  <div className="mt-3 border-t border-border pt-3">
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t("allow_to_collaborate_with")}</p>
                    {others.map((o) => {
                      const allowed = (p.collab_with_coach_ids || []).includes(o.id);
                      return (
                        <div key={o.id} className="flex items-center gap-3 py-2">
                          <GitBranch className="h-4 w-4 shrink-0 text-primary" />
                          <span className="flex-1 text-sm text-foreground">{o.name}</span>
                          <Switch data-testid={`toggle-collab-${c.id}-${o.id}`} checked={allowed} onCheckedChange={(v) => toggleCollab(c.id, o.id, v)} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex items-start gap-2 rounded-2xl border border-border bg-secondary/20 p-3">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">{t("even_in_a_3_way_chat_each_coach_only")}</p>
          </div>
        </div>
      )}
    </div>
  );
}
